import type { Project } from "../types/task";
import { ref } from "vue";
import { posthogService } from "./posthogService";

const STORAGE_KEY = "timeTracker_projects";

// Create a reactive reference to store projects
export const projects = ref<Project[]>([]);

// Initialize projects from localStorage
const projectsJson = localStorage.getItem(STORAGE_KEY);
if (!projectsJson) {
  projects.value = [];
  localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
} else {
  projects.value = JSON.parse(projectsJson);
}

class ProjectService {
  getProjects(): Project[] {
    return projects.value.filter((project) => !project.deleted);
  }

  getAllProjects(): Project[] {
    return projects.value;
  }

  saveProjects(newProjects: Project[]): void {
    projects.value = newProjects;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newProjects));
  }

  getProjectById(id: string): Project | undefined {
    return projects.value.find((project) => project.id === id && !project.deleted);
  }

  getProjectByName(name: string): Project | undefined {
    return projects.value.find(
      (project) => project.name === name && !project.deleted
    );
  }

  createProject(name: string): Project {
    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error("Project name cannot be empty");
    }

    // Check if project already exists (including soft deleted ones)
    const existingProject = projects.value.find(
      (project) => project.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingProject) {
      if (existingProject.deleted) {
        // Restore the soft deleted project
        existingProject.deleted = false;
        this.saveProjects([...projects.value]);
        return existingProject;
      }
      return existingProject;
    }

    // Generate ID from name (lowercase, replace spaces with hyphens)
    const id = trimmedName.toLowerCase().replace(/\s+/g, "-");

    const newProject: Project = { id, name: trimmedName };
    this.saveProjects([...projects.value, newProject]);

    // Track project creation
    posthogService.trackProjectCreated?.(trimmedName);

    return newProject;
  }

  updateProject(id: string, name: string): Project | undefined {
    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error("Project name cannot be empty");
    }

    const projectIndex = projects.value.findIndex((project) => project.id === id);

    if (projectIndex === -1) return undefined;

    // Check if another project with the same name exists
    const existingProject = projects.value.find(
      (project) =>
        project.id !== id &&
        project.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingProject) {
      throw new Error("A project with this name already exists");
    }

    const updatedProject: Project = { id, name: trimmedName };
    const newProjects = [...projects.value];
    newProjects[projectIndex] = updatedProject;
    this.saveProjects(newProjects);
    return updatedProject;
  }

  softDeleteProject(id: string): boolean {
    const projectIndex = projects.value.findIndex((project) => project.id === id);

    if (projectIndex === -1) return false;

    const newProjects = [...projects.value];
    newProjects[projectIndex] = { ...newProjects[projectIndex], deleted: true };
    this.saveProjects(newProjects);

    // Track project deletion
    const projectToDelete = projects.value[projectIndex];
    if (projectToDelete) {
      posthogService.trackProjectDeleted?.(projectToDelete.name);
    }

    return true;
  }

  restoreProject(id: string): boolean {
    const projectIndex = projects.value.findIndex((project) => project.id === id);

    if (projectIndex === -1) return false;

    const newProjects = [...projects.value];
    newProjects[projectIndex] = { ...newProjects[projectIndex], deleted: false };
    this.saveProjects(newProjects);
    return true;
  }

  deleteProject(id: string): boolean {
    const filteredProjects = projects.value.filter((project) => project.id !== id);

    if (filteredProjects.length === projects.value.length) return false;

    this.saveProjects(filteredProjects);
    return true;
  }

  getOrCreateProject(name: string): Project {
    return this.getProjectByName(name) || this.createProject(name);
  }
}

export const projectService = new ProjectService();


